import { useEffect, useState } from 'react';

const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || `${response.status} ${response.statusText}`);
    }
    return response.json();
};

const statusLabels = {
    'available': 'Libre',
    'occupied': 'Occupée',
    'reserved': 'Réservée',
    'cleaning': 'Nettoyage',
    'inactive': 'Inactive',
};

const statusColors = {
    'available': { bg: '#dcfce7', color: '#16a34a' },
    'occupied': { bg: '#dbeafe', color: '#0284c7' },
    'reserved': { bg: '#fce7f3', color: '#be185d' },
    'cleaning': { bg: '#fef3c7', color: '#d97706' },
    'inactive': { bg: '#f3f4f6', color: '#6b7280' },
};

const ensureArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object' && value.data && Array.isArray(value.data)) return value.data;
    if (typeof value === 'object') return Object.values(value);
    return [];
};

const normalizeTable = (table) => {
    const activeOrder = table.order || table.activeOrder;
    return {
        id: table.id,
        name: table.name || table.number,
        number: table.number,
        capacity: table.capacity,
        zone_id: table.zone_id ?? table.zone?.id ?? null,
        zone: table.zone,
        status: table.status,
        is_active: table.is_active,
        qr_code: table.qr_code,
        order: activeOrder
            ? {
                  id: activeOrder.id,
                  status: activeOrder.status,
                  notes: activeOrder.notes,
                  items: (activeOrder.items || []).map((item) => ({
                      menuItemId: item.menuItemId || item.dish_id,
                      quantity: item.quantity,
                      notes: item.notes,
                  })),
              }
            : null,
    };
};

export default function TablesTab({ tables, setTables }) {
    const [zones, setZones] = useState([]);
    const [formMode, setFormMode] = useState(null); // null, 'table', 'zone'
    const [editingTable, setEditingTable] = useState(null);
    const [editingZone, setEditingZone] = useState(null);
    const [showZoneForm, setShowZoneForm] = useState(false);
    const [tableForm, setTableForm] = useState({
        number: '',
        capacity: 2,
        zone_id: null,
        status: 'available',
        is_active: true,
    });
    const [zoneForm, setZoneForm] = useState({
        name: '',
        description: '',
        is_active: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [localTables, setLocalTables] = useState(() => ensureArray(tables).map(normalizeTable));

    useEffect(() => {
        setLocalTables(ensureArray(tables).map(normalizeTable));
    }, [tables]);

    // Charger les zones au montage
    useEffect(() => {
        const loadZones = async () => {
            try {
                const data = await fetchJson('/api/zones');
                setZones(ensureArray(data));
            } catch (err) {
                console.error('Erreur chargement zones:', err);
            }
        };
        loadZones();
    }, []);

    const resetTableForm = () => {
        setTableForm({
            number: '',
            capacity: 2,
            zone_id: zones.length ? zones[0].id : null,
            status: 'available',
            is_active: true,
        });
        setEditingTable(null);
    };

    const resetZoneForm = () => {
        setZoneForm({ name: '', description: '', is_active: true });
        setEditingZone(null);
    };

    const handleAddTable = () => {
        resetTableForm();
        setFormMode('table');
    };

    const handleEditTable = (table) => {
        setFormMode('table');
        setEditingTable(table);
        setTableForm(table);
    };

    const handleSaveTable = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (editingTable) {
                await fetchJson(`/api/tables/${editingTable.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(tableForm),
                });
            } else {
                await fetchJson('/api/tables', {
                    method: 'POST',
                    body: JSON.stringify(tableForm),
                });
            }
            const updatedTables = await fetchJson('/api/tables');
            const normalized = ensureArray(updatedTables).map(normalizeTable);
            setTables(normalized);
            setLocalTables(normalized);
            resetTableForm();
            setFormMode(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTable = async (tableId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette table ?')) return;

        setLoading(true);
        try {
            await fetchJson(`/api/tables/${tableId}`, { method: 'DELETE' });
            const updatedTables = await fetchJson('/api/tables');
            const normalized = ensureArray(updatedTables).map(normalizeTable);
            setTables(normalized);
            setLocalTables(normalized);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeTableStatus = async (tableId, newStatus) => {
        try {
            await fetchJson(`/api/tables/${tableId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: newStatus }),
            });
            const updatedTables = await fetchJson('/api/tables');
            const normalized = ensureArray(updatedTables).map(normalizeTable);
            setTables(normalized);
            setLocalTables(normalized);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAddZone = () => {
        setShowZoneForm(true);
        resetZoneForm();
    };

    const handleSaveZone = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (editingZone) {
                await fetchJson(`/api/zones/${editingZone.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(zoneForm),
                });
            } else {
                await fetchJson('/api/zones', {
                    method: 'POST',
                    body: JSON.stringify(zoneForm),
                });
            }
            const updatedZones = await fetchJson('/api/zones');
            setZones(ensureArray(updatedZones));
            setShowZoneForm(false);
            resetZoneForm();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteZone = async (zoneId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) return;

        setLoading(true);
        try {
            await fetchJson(`/api/zones/${zoneId}`, { method: 'DELETE' });
            const updatedZones = await fetchJson('/api/zones');
            setZones(updatedZones);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getTablesByZone = (zoneId) => {
        return localTables.filter((t) => {
            const tableZoneId = t.zone_id ?? t.zone?.id;
            return tableZoneId != null && String(tableZoneId) === String(zoneId);
        });
    };

    const getTablesWithoutZone = () => {
        return localTables.filter((t) => {
            const tableZoneId = t.zone_id ?? t.zone?.id;
            return tableZoneId == null || tableZoneId === '';
        });
    };

    const totalTables = localTables.length;

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-blue-400 bg-blue-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-blue-400">Gestion</p>
                        <h2 className="text-3xl font-semibold text-blue-900">Tables et zones</h2>
                    </div>
                    <p className="max-w-xl text-sm text-blue-600">
                        Gérez vos tables, zones de restaurant, capacités et statuts.
                    </p>
                </div>
            </section>

            {error && (
                <div className="rounded-3xl border border-red-400 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
                {/* Zones et Tables */}
                <div className="space-y-4">
                    {/* Zones */}
                    <div className="rounded-3xl border border-blue-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-blue-900">Zones</h3>
                            <button
                                onClick={handleAddZone}
                                className="rounded-3xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500"
                            >
                                + Zone
                            </button>
                        </div>

                        <div className="space-y-2">
                            {zones.map(zone => (
                                <div key={zone.id} className="flex items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 p-3">
                                    <div>
                                        <p className="font-medium text-blue-900">{zone.name}</p>
                                        <p className="text-xs text-blue-600">{zone.description}</p>
                                        <p className="text-xs text-blue-500">{zone.tables?.length || 0} table(s)</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setEditingZone(zone);
                                                setZoneForm(zone);
                                                setShowZoneForm(true);
                                            }}
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDeleteZone(zone.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tables overview */}
                    <div className="rounded-3xl border border-blue-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-semibold text-blue-900">Tables</h3>
                                <p className="text-sm text-blue-600">Ajoute, modifie ou supprime des tables par zone.</p>
                            </div>
                            <button
                                onClick={() => {
                                    handleAddTable();
                                    setShowZoneForm(false);
                                }}
                                className="rounded-3xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500"
                            >
                                + Nouvelle table
                            </button>
                        </div>

                        {!totalTables && (
                            <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-4 text-sm text-blue-600">
                                Aucune table créée pour le moment. Cliquez sur <strong>+ Nouvelle table</strong> pour en ajouter.
                            </div>
                        )}
                    </div>

                    {/* Tables par zone */}
                    {zones.map(zone => (
                        <div key={zone.id} className="rounded-3xl border border-blue-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                            <h4 className="mb-3 font-semibold text-blue-900">{zone.name}</h4>
                            <div className="space-y-2">
                                {getTablesByZone(zone.id).map(table => (
                                    <div key={table.id} className="flex items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 p-3">
                                        <div className="flex-1">
                                            <p className="font-medium text-blue-900">Table {table.number}</p>
                                            <p className="text-xs text-blue-600">{table.capacity} couverts</p>
                                            <select
                                                value={table.status}
                                                onChange={(e) => handleChangeTableStatus(table.id, e.target.value)}
                                                style={{
                                                    backgroundColor: statusColors[table.status]?.bg,
                                                    color: statusColors[table.status]?.color,
                                                }}
                                                className="mt-1 rounded-2xl px-2 py-1 text-xs font-medium"
                                            >
                                                {Object.entries(statusLabels).map(([key, label]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEditTable(table)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTable(table.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Tables sans zone */}
                    {getTablesWithoutZone().length > 0 && (
                        <div className="rounded-3xl border border-blue-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                            <h4 className="mb-3 font-semibold text-blue-900">Sans zone</h4>
                            <div className="space-y-2">
                                {getTablesWithoutZone().map(table => (
                                    <div key={table.id} className="flex items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 p-3">
                                        <div className="flex-1">
                                            <p className="font-medium text-blue-900">Table {table.number}</p>
                                            <p className="text-xs text-blue-600">{table.capacity} couverts</p>
                                            <select
                                                value={table.status}
                                                onChange={(e) => handleChangeTableStatus(table.id, e.target.value)}
                                                style={{
                                                    backgroundColor: statusColors[table.status]?.bg,
                                                    color: statusColors[table.status]?.color,
                                                }}
                                                className="mt-1 rounded-2xl px-2 py-1 text-xs font-medium"
                                            >
                                                {Object.entries(statusLabels).map(([key, label]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEditTable(table)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTable(table.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Formulaires */}
                <div className="space-y-4">
                    {/* Formulaire Table */}
                    {formMode === 'table' && (
                        <div className="rounded-3xl border border-blue-400 bg-blue-50/90 p-5 shadow-xl shadow-slate-950/20">
                            <h3 className="mb-4 text-xl font-semibold text-blue-900">
                                {editingTable ? 'Éditer table' : 'Nouvelle table'}
                            </h3>
                            <form className="space-y-4" onSubmit={handleSaveTable}>
                                <div>
                                    <label className="text-sm text-blue-600">Numéro</label>
                                    <input
                                        value={tableForm.number}
                                        onChange={(e) => setTableForm({ ...tableForm, number: e.target.value })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Ex. A1, Table 5"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-blue-600">Capacité (couverts)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={tableForm.capacity}
                                        onChange={(e) => setTableForm({ ...tableForm, capacity: Number(e.target.value) })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-blue-600">Zone</label>
                                    <select
                                        value={tableForm.zone_id ?? ''}
                                        onChange={(e) => setTableForm({ ...tableForm, zone_id: e.target.value ? Number(e.target.value) : null })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">-- Sans zone --</option>
                                        {zones.map(zone => (
                                            <option key={zone.id} value={zone.id}>{zone.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm text-blue-600">Statut</label>
                                    <select
                                        value={tableForm.status}
                                        onChange={(e) => setTableForm({ ...tableForm, status: e.target.value })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Object.entries(statusLabels).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-blue-600">
                                    <input
                                        type="checkbox"
                                        checked={tableForm.is_active}
                                        onChange={(e) => setTableForm({ ...tableForm, is_active: e.target.checked })}
                                    />
                                    Active
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 rounded-3xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                                    >
                                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            resetTableForm();
                                            setFormMode(null);
                                        }}
                                        className="flex-1 rounded-3xl border border-blue-400 bg-white px-4 py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Formulaire Zone */}
                    {showZoneForm && (
                        <div className="rounded-3xl border border-blue-400 bg-blue-50/90 p-5 shadow-xl shadow-slate-950/20">
                            <h3 className="mb-4 text-xl font-semibold text-blue-900">
                                {editingZone ? 'Éditer zone' : 'Nouvelle zone'}
                            </h3>
                            <form className="space-y-4" onSubmit={handleSaveZone}>
                                <div>
                                    <label className="text-sm text-blue-600">Nom</label>
                                    <input
                                        value={zoneForm.name}
                                        onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Ex. Terrasse, Salle VIP"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-blue-600">Description</label>
                                    <textarea
                                        value={zoneForm.description}
                                        onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })}
                                        className="mt-2 w-full rounded-3xl border border-blue-400 bg-white px-4 py-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Description optionnelle"
                                        rows="2"
                                    />
                                </div>
                                <label className="flex items-center gap-2 text-sm text-blue-600">
                                    <input
                                        type="checkbox"
                                        checked={zoneForm.is_active}
                                        onChange={(e) => setZoneForm({ ...zoneForm, is_active: e.target.checked })}
                                    />
                                    Active
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 rounded-3xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                                    >
                                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowZoneForm(false);
                                            resetZoneForm();
                                        }}
                                        className="flex-1 rounded-3xl border border-blue-400 bg-white px-4 py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Bouton ajouter table */}
                    {formMode !== 'table' && (
                        <button
                            onClick={handleAddTable}
                            className="w-full rounded-3xl bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-500"
                        >
                            + Nouvelle table
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
